import { useEffect, useRef } from 'react';
import { MascotUfo } from './MascotUfo';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function WaitingMascotArena() {
  const arenaRef = useRef<HTMLDivElement>(null);
  const shipRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const arenaEl = arenaRef.current;
    const shipEl = shipRef.current;
    if (!arenaEl || !shipEl) return;
    const arena: HTMLDivElement = arenaEl;
    const ship: HTMLSpanElement = shipEl;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pointer = { x: 0, y: 0, inside: false };
    const state = {
      angle: -Math.PI / 2,
      direction: 1,
      targetDirection: 1,
      fleeX: 0,
      fleeY: 0,
      nextJukeAt: 0,
      jukeX: 0,
      jukeY: 0,
      speedFactor: 1,
      directionLockUntil: 0,
      returnToClockwiseAt: 0,
    };
    let frameId = 0;
    let last = performance.now();
    let boopTimer = 0;

    function readPointer(e: PointerEvent) {
      const rect = arena.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.inside = true;
    }

    function onPointerLeave() {
      pointer.inside = false;
      state.returnToClockwiseAt = performance.now() + 1800;
    }

    function onPointerDown(e: PointerEvent) {
      readPointer(e);
      ship.dataset.boop = 'true';
      window.clearTimeout(boopTimer);
      boopTimer = window.setTimeout(() => {
        delete ship.dataset.boop;
      }, 260);
    }

    function placeStatic() {
      const rect = arena.getBoundingClientRect();
      const x = rect.width * 0.66;
      const y = rect.height * 0.28;
      ship.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) rotate(4deg)`;
    }

    function animate(now: number) {
      const rect = arena.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      if (width <= 0 || height <= 0) {
        frameId = window.requestAnimationFrame(animate);
        return;
      }

      const dt = Math.min(34, now - last);
      last = now;

      const radiusX = Math.min(Math.max(width * 0.43, 180), Math.max(180, width / 2 - 36));
      const radiusY = clamp(height * 0.4, 56, 82);
      const centerX = width / 2;
      const centerY = height / 2;
      const orbitX = centerX + Math.cos(state.angle) * radiusX;
      const orbitY = centerY + Math.sin(state.angle) * radiusY;

      const dx = orbitX + state.fleeX - pointer.x;
      const dy = orbitY + state.fleeY - pointer.y;
      const distance = Math.hypot(dx, dy) || 1;
      const nearness = pointer.inside ? clamp((130 - distance) / 130, 0, 1) : 0;
      let pathNearness = 0;

      if (pointer.inside) {
        const lookahead = 0.56 + nearness * 0.32;
        const clockwiseX = centerX + Math.cos(state.angle + lookahead) * radiusX;
        const clockwiseY = centerY + Math.sin(state.angle + lookahead) * radiusY;
        const counterX = centerX + Math.cos(state.angle - lookahead) * radiusX;
        const counterY = centerY + Math.sin(state.angle - lookahead) * radiusY;
        const clockwiseDistance = Math.hypot(clockwiseX - pointer.x, clockwiseY - pointer.y);
        const counterDistance = Math.hypot(counterX - pointer.x, counterY - pointer.y);
        pathNearness = clamp((150 - Math.min(clockwiseDistance, counterDistance)) / 150, 0, 1);
        if (Math.max(nearness, pathNearness) > 0.12) {
          const nextDirection = clockwiseDistance >= counterDistance ? 1 : -1;
          if (nextDirection === state.targetDirection || now > state.directionLockUntil) {
            state.targetDirection = nextDirection;
            state.directionLockUntil = now + 820;
          }
        }
      } else if (now > state.returnToClockwiseAt) {
        state.targetDirection = 1;
      }

      state.direction += (state.targetDirection - state.direction) * 0.14;
      const baseSpeed = 0.00062;
      const dodgeEnergy = Math.max(nearness, pathNearness * 0.55);
      const obstacleBrake = pointer.inside && pathNearness > 0.34 && nearness < 0.24;
      const targetSpeedFactor = obstacleBrake ? 0.16 : 1;
      state.speedFactor += (targetSpeedFactor - state.speedFactor) * 0.12;
      state.angle += state.direction * (baseSpeed + dodgeEnergy * 0.0012) * state.speedFactor * dt;

      let targetFleeX = 0;
      let targetFleeY = 0;
      if (nearness > 0.1) {
        const awayX = dx / distance;
        const awayY = dy / distance;
        targetFleeX = awayX * (24 + nearness * 46);
        targetFleeY = awayY * (12 + nearness * 28);
        if (nearness > 0.62 && now > state.nextJukeAt) {
          state.nextJukeAt = now + 260 + Math.random() * 220;
          state.jukeX = (Math.random() - 0.5) * 42;
          state.jukeY = (Math.random() - 0.5) * 24;
        }
      } else {
        state.jukeX *= 0.82;
        state.jukeY *= 0.82;
      }

      state.fleeX += (targetFleeX + state.jukeX - state.fleeX) * 0.12;
      state.fleeY += (targetFleeY + state.jukeY - state.fleeY) * 0.12;

      const bob = Math.sin(now / 220) * (2 + nearness * 3);
      const x = clamp(orbitX + state.fleeX, 34, width - 34);
      const y = clamp(orbitY + state.fleeY + bob, 30, height - 30);
      const tilt = clamp(Math.cos(state.angle) * 10 + state.fleeX * 0.08, -18, 18);
      const scale = 1 + dodgeEnergy * 0.12;

      ship.dataset.mood = dodgeEnergy > 0.28 ? 'alert' : 'calm';
      ship.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) rotate(${tilt}deg) scale(${scale})`;
      frameId = window.requestAnimationFrame(animate);
    }

    arena.addEventListener('pointermove', readPointer);
    arena.addEventListener('pointerleave', onPointerLeave);
    arena.addEventListener('pointerdown', onPointerDown);

    if (reduceMotion) {
      placeStatic();
    } else {
      frameId = window.requestAnimationFrame(animate);
    }

    return () => {
      arena.removeEventListener('pointermove', readPointer);
      arena.removeEventListener('pointerleave', onPointerLeave);
      arena.removeEventListener('pointerdown', onPointerDown);
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(boopTimer);
    };
  }, []);

  return (
    <div ref={arenaRef} className="waiting-mascot-arena" aria-hidden="true">
      <MascotUfo ref={shipRef} className="waiting-ufo" />
    </div>
  );
}
